/**
 * World simulation: oil price path, foam cost index, competitive pressure,
 * seasonality. Pure functions of state + per-day seeded RNG streams.
 */

import { clamp } from './format';
import {
  FOAM_OIL_COUPLING,
  OIL_MAX,
  OIL_MEAN_BASE,
  OIL_MIN,
  SEASONALITY,
} from './constants';
import type { Difficulty, WorldState } from './types';
import { dayToDate } from './format';

/** Ornstein–Uhlenbeck-style mean-reverting step for the oil spot price. */
export function stepOil(world: WorldState, rng: () => number, volatilityMult: number): void {
  // Slowly wandering long-run mean: regimes shift over months.
  const drift = (rng() - 0.5) * 0.08;
  // BALANCE (v2): sigma raised 0.85 → 1.6 and pull 0.20 → 0.15 so the spot
  // price visibly wanders ±$8–12 over weeks instead of hugging the anchor —
  // the lagged surcharge mechanic was invisible at the old variance.
  world.oil = clamp(
    world.oil + 0.015 * (OIL_MEAN_BASE + regimeOffset(world) - world.oil) * 10 +
      gauss(rng) * 1.6 * volatilityMult +
      drift,
    OIL_MIN,
    OIL_MAX,
  );
}

function gauss(rng: () => number): number {
  // Box–Muller is fine here; called once per day.
  const u1 = Math.max(rng(), 1e-9);
  const u2 = rng();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

function regimeOffset(world: WorldState): number {
  // Map competition pressure & market growth into a mild macro bias so
  // chaotic runs feel thematically coupled (hot market ⇒ energy demand).
  return clamp((world.competition - 1) * 8, -6, 12);
}

/** Foam index mean-reverts to a petro-coupled anchor; shocks are event-driven. */
export function stepFoamIndex(world: WorldState): void {
  const anchor = 100 * (1 + FOAM_OIL_COUPLING * ((world.oilLagged - 80) / 80));
  world.foamIndex = clamp(world.foamIndex + 0.03 * (anchor - world.foamIndex), 60, 210);
}

/** Update the 21–56 day trailing average of oil that drives surcharges. */
export function updateOilLagged(world: WorldState, oilHistory: number[], today: number): void {
  const from = Math.max(0, today - 56);
  const to = Math.max(0, today - 21);
  let sum = 0;
  let n = 0;
  for (let i = from; i <= to && i < oilHistory.length; i++) {
    sum += oilHistory[i];
    n++;
  }
  if (n > 0) world.oilLagged = sum / n;
}

/**
 * Competitive intensity random walk: DTC categories oscillate between
 * funding-winter docility and VC-fueled bloodbaths (175+ brands at peak).
 */
export function stepCompetition(world: WorldState, rng: () => number, base: number): void {
  const pull = 0.01 * (base - world.competition);
  world.competition = clamp(world.competition + pull + (rng() - 0.5) * 0.012, 0.6, 1.85);
}

/** Retail-calendar multiplier for the given day index. */
export function seasonalMultiplier(day: number): number {
  const d = dayToDate(day);
  const doy = Math.floor((d.getTime() - Date.UTC(d.getUTCFullYear(), 0, 1)) / 86_400_000);
  let mult = 1;
  for (const w of SEASONALITY) {
    if (doy >= w.startDoy && doy <= w.endDoy) mult = Math.max(mult, w.mult);
  }
  return mult;
}

/** Ad-auction heat: Q4 gets expensive everywhere (CPM inflation). */
export function adAuctionHeat(day: number): number {
  const d = dayToDate(day);
  const month = d.getUTCMonth();
  if (month === 10) return 1.35; // November
  if (month === 11) return 1.18; // December
  return 1.0;
}

/** Category demand growth applied gradually (default ~6.5%/yr, per industry CAGR). */
export function growMarket(world: WorldState): void {
  const dailyGrowth = world.marketGrowth / 365;
  // Growth compounds into a demand scalar tracked via competition relief:
  // growing pie slightly offsets competitive decay each day.
  world.competition = clamp(world.competition - dailyGrowth * 0.35, 0.6, 1.85);
}

export function initialWorld(difficulty: Difficulty, seed: number): { world: WorldState; oilHistory: number[] } {
  // Seed the run in a plausible mid-2020s regime.
  const world: WorldState = {
    oil: 76,
    oilLagged: 75,
    foamIndex: 100,
    competition: difficulty === 'realistic' ? 1.45 : difficulty === 'hard' ? 1.2 : 1.0,
    marketGrowth: 0.065,
  };
  // Pre-roll ~70 days of history so lags are warm on day one.
  const oilHistory: number[] = [];
  let o = 74;
  for (let i = 0; i < 70; i++) {
    o = clamp(o + (Math.sin(i * 12.9898 + seed) % 1) * 0.4, OIL_MIN, OIL_MAX);
    oilHistory.push(o);
  }
  world.oilLagged =
    oilHistory.slice(-56).reduce((a, b) => a + b, 0) / Math.min(56, oilHistory.length);
  return { world, oilHistory };
}
